import axios from 'axios';
import utils, { AppVersion } from '../lib/utils';
import moment from 'moment';
import WithBootedClient from '../lib/WithBootedClient';
import { ICountItem } from '../models/ICountItem';
import IJwt from '../models/IJwt';
import { ITask } from '../models/tasks/ITask';
import { ITaskFilter } from '../models/tasks/ITaskFilter';
import { IUser } from '../models/users/IUser';
import { IUserDashboardCountResponse } from '../models/users/IUserDashboardCountResponse';
import { IUserTasksStatsResponse } from '../models/users/IUserTasksStatsResponse';
import AuthenticationClient from './AuthenticationClient';
import RESTClient, { IArrayRestResponse } from './RESTClient';
import Secrets from '@yumi/secrets';
import BreakAdjustmenAlertClient from '../pages/tasks/cl-task-stock-break-alert/api/BreakAdjustmenAlertClient';
import { TaskEnumBreakAdjustment } from '../pages/tasks/cl-task-stock-break-alert/utils';

interface IConfig {
  baseURL: string;
}

const formatTaskQuery = (filter?: ITaskFilter) => {
  let query = '';
  if (filter) {
    const keys = Object.keys(filter);
    query = keys.reduce((acc, current, idx) => {
      if (idx === 0) return `${acc}${current}=${(filter as any)[current]}`;
      return `${acc}&${current}=${(filter as any)[current]}`;
    }, '?');
  }
  return query;
};

class UserClient extends RESTClient implements WithBootedClient {
  constructor(config: IConfig) {
    super({ baseURL: config.baseURL });
  }

  async boot() { }

  async getById(userId: string) {
    const response = await axios.get(`${Secrets.REACT_MDH_USERS_API}/external-apps/users/${userId}`, {
      headers: {
        Authorization: `Bearer ${AuthenticationClient.state.user?.access_token}`,
        'App-Version': AppVersion,
        ...this.getCloudflareHeaders(),
      },
    });
    return response.data;
  }

  async updateNotificationTokenWithJwt(device_token: string, jwt: IJwt): Promise<void> {
    const response = await axios.patch(
      `${Secrets.REACT_APP_API_ENDPOINT}/users/me`,
      { device_token },
      {
        headers: {
          Authorization: `${jwt.token_type.toLowerCase()} ${jwt.access_token}`,
          'App-Version': AppVersion,
          ...this.getCloudflareHeaders(),
        },
      },
    );
    return response.data;
  }

  async me(): Promise<IUser> {
    const response = await this.axios.get('/users/me/info');
    return response?.data || {};
  }

  async countTaskByCategoires(taskType: string, filter?: ITaskFilter): Promise<Array<ICountItem> | undefined> {
    if (filter && filter.meta_data === undefined) {
      delete filter.meta_data;
    }
    if (!filter || !filter?.sameOrAfterDate) {
      filter = {
        ...filter,
        sameOrAfterDate: moment().format('YYYY-MM-DD').toString(),
      };
    }
    if (taskType === 'RACKREVIEW') {
      delete filter.sameOrAfterDate;
    }
    const query = formatTaskQuery(filter);
    try {
      const response = await this.axios.get(`users/me/taskType/${taskType}/categories/count${query}`);
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  combineTaskResponses(responses: IUserDashboardCountResponse[]) {
    const result: any = {
      statistics: { pendings: 0, totals: 0 },
      tasks: { data: [], limit: 0, offset: 0, total: 0 },
      links: [],
    };
    responses.forEach((response) => {
      result.statistics.pendings = result.statistics.pendings + response.statistics.pendings;
      result.statistics.totals = result.statistics.totals + response.statistics.totals;

      if (response.tasks) {
        result.tasks.data = [...result.tasks.data, ...response.tasks.data];
        result.tasks.limit = response.tasks?.limit || 0;
        result.tasks.offset = response.tasks?.offset || 0;
        result.tasks.total = result.tasks.total + (response.tasks?.total ?? 0);
      }
    });
    return result;
  }

  async getDashboardCount(filter?: ITaskFilter): Promise<IUserDashboardCountResponse | undefined> {
    if (!filter || !filter?.sameOrAfterDate) {
      filter = {
        ...filter,
        sameOrAfterDate: moment().format('YYYY-MM-DD').toString(),
      };
    }

    const query = formatTaskQuery(filter);
    try {
      const taskManagerfilter = {
        ...filter,
        taskType: 'TASKMANAGER',
      };
      if (taskManagerfilter.sameOrAfterDate) {
        delete taskManagerfilter['sameOrAfterDate'];
      }

      const lossplanResolutionfilter = {
        ...filter,
        taskType: 'LOSSPLANRESOLUTION',
        sameOrAfterDate: moment().subtract(1, 'days').format('YYYY-MM-DD').toString(),
      };

      const remateControlFilter = {
        ...filter,
        taskType: 'REMATECONTROL',
        sameOrAfterDate: moment().subtract(1, 'days').format('YYYY-MM-DD').toString(),
      };

      const remateFilter = {
        ...filter,
        taskType: 'REMATE',
        sameOrAfterDate: moment().subtract(2, 'days').format('YYYY-MM-DD').toString(),
      };

      const remateReleaseFilter = {
        ...filter,
        taskType: 'REMATERELEASE',
        sameOrAfterDate: moment().subtract(90, 'days').format('YYYY-MM-DD').toString(),
      };

      const postersFilter = {
        ...filter,
        taskType: 'POSTERS',
      };
      if (postersFilter.sameOrAfterDate) {
        delete postersFilter['sameOrAfterDate'];
      }
      if (postersFilter.sameOrBeforeDate) {
        delete postersFilter['sameOrBeforeDate'];
      }

      const taskRACKSfilter = {
        ...filter,
        taskType: 'RACKREVIEW',
      };

      if (taskRACKSfilter.sameOrAfterDate) {
        delete taskRACKSfilter.sameOrAfterDate;
      }

      const queryTM = formatTaskQuery(taskManagerfilter);
      const queryLPR = formatTaskQuery(lossplanResolutionfilter);
      const queryRCTRL = formatTaskQuery(remateControlFilter);
      const queryRemateTRL = formatTaskQuery(remateFilter);
      const queryRemateReleaseTRL = formatTaskQuery(remateReleaseFilter);
      const queryRACKs = formatTaskQuery(taskRACKSfilter);
      const queryPosters = formatTaskQuery(postersFilter);

      const promises = [
        this.axios.get(`/users/me/dashboard/count${query}`),
        this.axios.get(`/users/me/dashboard/count${queryTM}`),
        this.axios.get(`/users/me/dashboard/count${queryLPR}`),
        this.axios.get(`/users/me/dashboard/count${queryRCTRL}`),
        this.axios.get(`/users/me/dashboard/count${queryRemateTRL}`),
        this.axios.get(`/users/me/dashboard/count${queryRemateReleaseTRL}`),
        this.axios.get(`/users/me/dashboard/count${queryRACKs}`),
        this.axios.get(`/users/me/dashboard/count${queryPosters}`),
      ];

      const responses = await Promise.all(promises);
      const [
        response,
        taskManagerTasksResponse,
        lossplanResolutionResponse,
        remateControlResponse,
        remateResponse,
        remateReleaseResponse,
        responseRacks,
        responsePosters,
      ] = responses;

      const userInfo = AuthenticationClient.getInfo();
      const breakAdjustmentTasks: ICountItem[] = [];

      try {
        if (userInfo?.country && userInfo?.store) {
          const breakTaskTypes: TaskEnumBreakAdjustment[] = [];

          const managerRoles = ['breakadjustment.submanager', 'breakadjustment.manager', 'breakadjustment.business'];
          const operatorRoles = ['breakadjustment.tools', 'breakadjustment.write'];

          if (utils.hasAccessByRole(operatorRoles)) {
            breakTaskTypes.push(TaskEnumBreakAdjustment.BREAKADJUSTMENTALERT);
          }

          if (utils.hasAccessByRole(managerRoles)) {
            breakTaskTypes.push(TaskEnumBreakAdjustment.BREAKADJUSTMENTALERTFORAPPROVE);
          }

          if (breakTaskTypes.length > 0) {
            const breakSections = await Promise.all(
              breakTaskTypes.map((taskType) =>
                BreakAdjustmenAlertClient.getSectionList(userInfo.country, userInfo.store, taskType).catch(() => null),
              ),
            );

            breakSections.forEach((sectionsData, index) => {
              if (sectionsData && sectionsData.total > 0) {
                breakAdjustmentTasks.push({
                  name: breakTaskTypes[index],
                  total: sectionsData.total,
                });
              }
            });
          }
        }
      } catch (error) {
        console.error('Error fetching break adjustment tasks:', error);
      }

      let combinedTasks = this.combineTaskResponses(
        [
          response.data,
          taskManagerTasksResponse.data,
          lossplanResolutionResponse.data,
          remateControlResponse.data,
          remateResponse.data,
          remateReleaseResponse.data,
          responseRacks.data,
          responsePosters.data,
        ].filter((r): r is IUserDashboardCountResponse => r !== undefined),
      );
      if (breakAdjustmentTasks.length && combinedTasks) {
        breakAdjustmentTasks.forEach((bTask) => {
          const existingIndex = combinedTasks.tasks.data.findIndex((t: ICountItem) => t.name === bTask.name);
          if (existingIndex !== -1) {
            const previousTotal = combinedTasks.tasks.data[existingIndex].total || 0;
            combinedTasks.tasks.data[existingIndex].total = bTask.total;
            combinedTasks.tasks.total += (bTask.total - previousTotal);
          } else {
            combinedTasks.tasks.data.push(bTask);
            combinedTasks.tasks.total += bTask.total;
          }
        });
      }
      return combinedTasks;
    } catch (error) {
      return undefined;
    }
  }

  async getStats(filter?: ITaskFilter): Promise<IUserTasksStatsResponse> {
    if (!filter || !filter?.sameOrAfterDate) {
      filter = {
        ...filter,
        sameOrAfterDate: moment().format('YYYY-MM-DD').toString(),
      };
    }
    const query = formatTaskQuery(filter);
    const response = await this.axios.get(`/users/me/tasks/stats${query}`);
    return response.data;
  }

  async getTasks(filter?: ITaskFilter): Promise<IArrayRestResponse<ITask<any>>> {
    if (!filter || !filter?.sameOrAfterDate) {
      filter = {
        ...filter,
        sameOrAfterDate: moment().format('YYYY-MM-DD').toString(),
      };
    }
    if (filter?.taskType === 'RACKREVIEW') {
      delete filter.sameOrAfterDate;
    }
    const query = formatTaskQuery(filter);
    const url = encodeURI(`/users/me/tasks${query}`);
    const response = await this.axios.get(url);
    return response.data;
  }

  async getTasksWithFilter(filter?: ITaskFilter): Promise<IArrayRestResponse<ITask<any>>> {
    const query = formatTaskQuery(filter);
    const url = encodeURI(`/users/me/tasks${query}`);
    const response = await this.axios.get(url);
    return response.data;
  }

  async uploadPhotoUser(userId: string, base64Image: string, urlAvatar: string): Promise<void> {
    await this.axios.post(`/users/me/info/images`, {
      created_at: new Date(),
      image: base64Image,
      user: userId,
      urlAvatar: urlAvatar,
    });
  }

  async changeStore(store: string): Promise<void> {
    try {
      const options = {
        headers: {
          Authorization: `Bearer ${AuthenticationClient.state.user?.access_token}`,
          'App-Version': AppVersion,
          ...this.getCloudflareHeaders(),
        },
      };

      const result: any = await axios.put(
        `${Secrets.REACT_MDH_USERS_API}/app-bff/stores`,
        { store },
        options
      );

      if (result.status === 200) {
        await AuthenticationClient.loadUserInfo();
      } else {
        throw new Error(`Unexpected error changing store with status code : ${result.status}`);
      }
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to change store'
      );
    }
  }

  private getCloudflareHeaders() {
    return Secrets.REACT_APP_ENVIRONMENT === 'STAGING'
      ? {
        'CF-Access-Client-Id': Secrets.CF_ACCESS_CLIENT_ID,
        'CF-Access-Client-Secret': Secrets.CF_ACCESS_CLIENT_SECRET,
      }
      : {};
  }
}

export default new UserClient({
  baseURL: Secrets.REACT_APP_API_ENDPOINT!,
});
